import { GasMeterDetails } from "../../types/components.types";
import {
  MultiMprnResult,
  GasTableData,
  GasTableColumn,
  TableRow,
  TableCellValue,
} from "../../types/services.types";

// Format field names for display e.g., "currentSupplierRegEffectiveDate" formats to "Current Supplier Reg Effective Date".
// Unlike Electralink (snake_case), the Gas meter search backend returns camelCase field names too, so we need to handle the camelCase conversion.
// The trim() removes any leading space that would occur if a field starts with a capital letter (e.g., "PostCode" -> " Post Code" -> "Post Code").
export const formatFieldName = (field: string): string => {
  // Handle camelCase by inserting spaces before capital letters.
  const spacedField = field.replace(/([A-Z])/g, " $1");
  // Handle snake_case by replacing underscores with spaces.
  const withSpaces = spacedField.replace(/_/g, " ");
  // Capitalise first letter of each word
  return withSpaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .trim();
};

export const flattenGasMeterDetailsForTable = (results: MultiMprnResult[]): GasTableData => {
  // Build columns from the results.
  const columns: GasTableColumn[] = results.map((result) => ({
    mprn: result.mprn,
    hasError: !result.success,
  }));

  // Extract all unique field names from the successful results.
  const fieldSet = new Set<string>();
  results.forEach((result) => {
    if (result.success && result.gasMeterDetails) {
      Object.keys(result.gasMeterDetails).forEach((key) => {
        // Skip MPRN (as the MPRN/s are already in column headers).
        if (key !== "mprn") {
          fieldSet.add(key);
        }
      });
    }
  });

  // Add combined full address field.
  fieldSet.add("full_address");

  const fields = Array.from(fieldSet);

  // Build the table rows.
  const rows: TableRow[] = fields.map((field) => {
    const values: TableCellValue[] = results.map((result) => {
      if (!result.success) {
        // Display an error for the failed MPRNs.
        return {
          display: result.error ?? "Error",
          isError: true,
        };
      }

      if (!result.gasMeterDetails) {
        return {
          display: "",
          isError: false,
        };
      }

      // Handle combined full address field.
      if (field === "full_address") {
        const addressParts = [
          result.gasMeterDetails.houseName,
          result.gasMeterDetails.subBuildingName,
          result.gasMeterDetails.houseNumber,
          result.gasMeterDetails.dependentStreet,
          result.gasMeterDetails.street,
          result.gasMeterDetails.postTown,
          result.gasMeterDetails.postcode,
          result.gasMeterDetails.county,
        ];
        // Filter out empty/null/undefined values and join with comma and space.
        const fullAddress = addressParts
          .filter((part: unknown) => part !== "" && part !== null && part !== undefined)
          .join(", ");
        return {
          display: fullAddress,
          isError: false,
        };
      }

      // Handle regular fields.
      // The API returns numbers and nulls for some fields its types declare as strings, so the value is treated as unknown.
      const value: unknown = result.gasMeterDetails[field as keyof GasMeterDetails];

      if (value === undefined || value === null || value === "") {
        return {
          display: "",
          isError: false,
        };
      }

      // Always display a string, as the CSV export calls string methods on it.
      if (typeof value === "number" || typeof value === "boolean") {
        return {
          display: String(value),
          isError: false,
        };
      }

      if (typeof value !== "string") {
        return {
          display: JSON.stringify(value),
          isError: false,
        };
      }

      return {
        display: value,
        isError: false,
      };
    });

    return {
      field: field === "full_address" ? "Full Address" : formatFieldName(field),
      values,
    };
  });

  return { columns, rows };
};
